<template>
  <div class="w-full h-full px-4 py-4 bg-white rounded-b-lg">
    <div class="space-y-6 font-manrope">
      <!-- Instructions -->
      <section aria-labelledby="instructions-title" class="rounded-xl border border-gray-200">
        <div class="bg-gray-100 rounded-t-xl px-4 py-3 sm:px-5">
          <h2 id="instructions-title" class="font-semibold font-manrope text-gray-900">
            Instructions
          </h2>
        </div>
        <div class="relative">
          <div class="px-4 py-3 sm:px-5 overflow-x-auto">
            <div v-html="getCurrentDiscussion.instructions" class="instructions-container prose prose-sm sm:prose-base max-w-none break-words"></div>
          </div>
        </div>
      </section>

      <!-- Content & Multimedia -->
      <section aria-labelledby="resources-title" class="rounded-xl border border-gray-200">
        <div class="bg-gray-100 rounded-t-xl px-4 py-3 sm:px-5">
          <h2 id="resources-title" class="font-semibold font-manrope text-gray-900">
            Content & Multimedia
          </h2>
        </div>

        <div class="px-4 py-3 sm:px-5">
          <ul class="space-y-2">
            <li v-for="(resource, index) in visible_resource_questions" :key="index" class="flex items-start">
              <button
                type="button"
                class="underline text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                @click="openResourceLink(resource)"
              >
                {{ resource.resourceName }}
              </button>
            </li>
          </ul>

          <p v-if="!visible_resource_questions.length" class="text-sm text-gray-500">
            No content or multimedia has been created or activated.
          </p>
        </div>
      </section>

      <!-- Guiding Questions -->
      <section aria-labelledby="guiding-title" class="rounded-xl border border-gray-200">
        <div class="bg-gray-100 rounded-t-xl px-3 py-3 sm:px-5">
          <h2 id="guiding-title" class="font-semibold font-manrope text-gray-900">
            Guiding Questions
          </h2>
        </div>

        <div class="px-4 py-3 sm:px-3">
          <ol class="space-y-3 list-decimal pl-5">
            <li v-for="(guiding, index) in guiding_questions" :key="index" v-if="guiding.is_active" class="text-gray-900 break-words">
              {{ guiding.question }}
            </li>
          </ol>
          <p v-if="!visible_guiding_questions.length" class="text-sm text-gray-500">
            No guiding questions have been created or activated.
          </p>
        </div>
      </section>

      <!-- Assessment Criteria -->
      <section v-if="getCurrentDiscussion.enable_grading" aria-labelledby="assessment-title" class="rounded-xl border border-gray-200">
        <header class="bg-gray-100 rounded-t-xl px-4 py-3 sm:px-5">
          <h2 id="assessment-title" class="font-semibold font-manrope text-gray-900">
            Assessment Criteria
          </h2>
        </header>

        <div class="divide-y divide-gray-100 font-manrope">
          <details v-for="(criteria, index) in criterias" :key="index" class="group open:bg-gray-50/60">
            <summary class="flex items-start justify-between  gap-3 px-6 py-3 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400">
              <span class="text-sm text-gray-900 hover:underline font-medium">
                {{ criteria.title }}
              </span>
              <button class="ml-auto inline-flex h-5 w-5 text-xs items-center rounded-full justify-center transition-transform group-open:rotate-180" aria-hidden="true">
                <i class="fa-solid fa-chevron-down"></i>
              </button>
            </summary>
            <div class="px-6 py-3">
              <div v-if="criteria.description" class="text-sm text-gray-700 leading-relaxed html-content" v-html="typeof criteria.description === 'object' ? criteria.description.join('. ') : criteria.description"></div>
              <div v-if="criteria.points_description && criteria.points_description.length" class="mt-3">
                <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Levels
                </h3>
                <ul class="space-y-2">
                  <li v-for="(point, pIdx) in criteria.points_description" :key="pIdx" v-if="point.description !== ''" class="text-sm text-gray-800">
                    <span class="font-medium">{{ point.value }}</span>
                    <span class="text-gray-700"> — {{ point.description }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </details>
        </div>
      </section>
    </div>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex';
  import { filter, uniqBy } from 'lodash';
  import Popper from 'vue-popperjs';

  export default {
    data() {
      return {
        editor: null,
        isCriteriaPopperOpen: false,
      };
    },
    watch: {
      richText() {
        this.$nextTick(() => {
          this.adjustEditorHeight();
        });
      }
    },
    components: {
      RichTextEditor: () => import('@tinymce/tinymce-vue'),
      Popper,
    },
    computed: {
      ...mapGetters([
        'hasTeacherPermissionsInCourse',
        'getCurrentDiscussion',
        'getCourseDetail',
      ]),
      visible_resource_questions() {
        return this.getCurrentDiscussion.toggleable_resource_questions.filter(
          (resource) => {
            return resource.is_active;
          }
        );
      },
      guiding_questions() {
        return this.getCurrentDiscussion.toggleable_guiding_questions;
      },
      visible_guiding_questions() {
        return this.getCurrentDiscussion.toggleable_guiding_questions.filter(
          (guidance) => {
            return guidance.is_active;
          }
        );
      },
      tinyMceInit: function () {
        return {
          menubar: false,
          toolbar: false,
          readonly: true,
          selector: '#text-editor-verbal-prompt-tab',
          statusbar: false,
          content_style: "body { background-color: #fff; }",
          init_instance_callback: function (editor) {
            editor.setMode('readonly');
          }
        };
      },
      criterias() {
        const coursePoints = filter(
          this.getCurrentDiscussion.points_settings.peer_rewards,
          (reward) => {
            const { tag } = reward;
            const prefix = 'ld';
            return (
              tag.substring(0, 2) === prefix || tag.substring(0, 3) === 'all'
            );
          }
        );
        return uniqBy(
          [
            ...coursePoints,
            ...this.getCurrentDiscussion.points_settings.peer_rewards,
          ],
          'tag'
        ).filter((point) => point);
      },
    },
    methods: {
      openResourceLink(resource) {
        window.open(resource.resourceURL);
      },
    },
  };
</script>
<style>
  .instructions-container ol,
.instructions-container ul {
  margin-left: 12px;
  padding-left: 0; /* Para garantir que o margin-left seja aplicado corretamente */
}

.instructions-container ol > li,
.instructions-container ul > li {
  list-style-position: inside; /* Garante que o marcador apareça dentro da caixa de li */
  margin-left: 0; /* Evita um deslocamento indesejado dos itens */
}

.instructions-container ol > li {
  list-style-type: decimal; /* Para garantir que os números apareçam nas listas ordenadas */
}

.instructions-container ul > li {
  list-style-type: disc; /* Para garantir que os pontos apareçam nas listas não ordenadas */
}

</style>



// WEBPACK FOOTER //
// LDPromptTab.vue?c2b03fba